// Shapes of the agents API, from the SDK's typed client (never hand-written), and the pure helpers of
// the Agents and agent pages (grouping, the guardrails draft, the runs summary).
export type {
  Agent,
  AgentSummary,
  AgentTool,
  AgentsList,
  ModelRole,
  Person,
  ToolCatalog,
  ToolOperation,
} from '@cortex/sdk-agents-client';
import type { AgentSummary, ModelRole } from '@cortex/sdk-agents-client';

/** One run of an agent, as the host's runs port gives it (a row of the host's ledger). */
export interface LedgerRun {
  id: string | number;
  status: string;
  guardrail: unknown;
  task: string;
  startedAt: string;
  userEmail: string | null;
  toolCalls: unknown[];
  error: string | null;
  /** How the run ended (ADR 0039 §4) and what stopped it; absent on runs recorded before. */
  outcome?: string | null;
  stop?: { reason: string; budget?: string; check?: string } | null;
  harness?: { hash: string; mode: string } | null;
  /** The chat session the run was in (an evaluation's case runs in its own). */
  sessionId?: string | null;
  /** The run that handed it this task (the host's own agent delegating to a platform agent); null = asked in the chat. */
  parentRunId?: number | null;
  durationMs?: number | null;
  inputTokens?: number | null;
  outputTokens?: number | null;
}

export const AGENT_KEY_RE = /^[a-z][a-z0-9_-]{0,57}$/;
export const TOOL_NAME_RE = /^[a-z][a-z0-9_]{0,63}$/;

export const MODEL_ROLES: { value: ModelRole; label: string; help: string }[] = [
  { value: 'fast', label: 'Fast', help: 'Quick, inexpensive answers over a few tool calls.' },
  { value: 'standard', label: 'Standard', help: 'The platform default for project agents.' },
  { value: 'reasoning', label: 'Reasoning', help: 'Slower; for multi-step analysis.' },
];
export const roleLabel = (r: string) => MODEL_ROLES.find((m) => m.value === r)?.label ?? r;

/** The rail's sections, in the old page's order: mine, shared with me, the rest of the organization. */
export function groupAgents(agents: readonly AgentSummary[]) {
  const mine = agents.filter((a) => a.isOwner);
  const shared = agents.filter((a) => !a.isOwner && a.sharedWithMe);
  const org = agents.filter((a) => !a.isOwner && !a.sharedWithMe);
  return [
    { id: 'mine', label: 'My agents', items: mine },
    { id: 'shared', label: 'Shared with me', items: shared },
    { id: 'org', label: 'This project', items: org },
  ].filter((g) => g.items.length);
}

export const visibilityText = (a: { visibility: string; sharedCount: number }) =>
  a.visibility === 'organization'
    ? 'Visible to the organization'
    : a.sharedCount
      ? `Private · shared with ${a.sharedCount}`
      : 'Private';

/** A key from a display name: `Order Helper!` → `order_helper`. */
export function keyFromTitle(title: string): string {
  const k = title
    .toLowerCase()
    .normalize('NFKD')
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^[^a-z]+/, '')
    .replace(/_+$/, '')
    .slice(0, 58);
  return k || 'agent';
}

/** A tool name from an operationId: `listDataElements` → `list_data_elements`. */
export function toolNameOf(operationId: string): string {
  const n = operationId
    .replace(/([a-z0-9])([A-Z])/g, '$1_$2')
    .replace(/[^A-Za-z0-9]+/g, '_')
    .toLowerCase()
    .replace(/^[^a-z]+/, '')
    .replace(/_+/g, '_')
    .replace(/_$/, '')
    .slice(0, 64);
  return n || 'tool';
}

/** The parameters of a tool's input schema, as chips: `name*:type`. */
export function paramsOf(
  schema: Record<string, unknown> | undefined,
): { name: string; type: string; required: boolean }[] {
  const props = (schema?.properties ?? {}) as Record<string, { type?: unknown }>;
  const req = new Set(Array.isArray(schema?.required) ? (schema.required as string[]) : []);
  return Object.entries(props).map(([name, p]) => ({
    name,
    type: Array.isArray(p?.type) ? p.type.join('|') : typeof p?.type === 'string' ? p.type : 'object',
    required: req.has(name),
  }));
}

/**
 * A tool's description as a person reads it: its own words without the technical tail a migrated tool carries
 * ("Arguments (put them in `input`): {…json schema…}", for the agent), and the names of its inputs — that tail's
 * properties, else the operation's parameters (an operation that only takes a body names none).
 */
export function toolText(
  description: string,
  schema?: Record<string, unknown>,
): { what: string; inputs: string[] } {
  const m = /\s*Arguments\b[^:{]*:\s*(\{[\s\S]*)$/.exec(description);
  if (m) {
    let props: string[] = [];
    try {
      props = Object.keys((JSON.parse(m[1]!) as { properties?: object }).properties ?? {});
    } catch {
      /* not JSON: the tail is cut, its inputs unknown */
    }
    return { what: description.slice(0, m.index).trim(), inputs: props };
  }
  const params = paramsOf(schema).map((p) => p.name);
  return { what: description.trim(), inputs: params.length === 1 && params[0] === 'body' ? [] : params };
}

// ── Guardrails (the old page's draft mapping) ───────────────────────────────────────────────────

export interface GuardDraft {
  scope: string;
  grounding: 'none' | 'tools';
  verify: boolean;
  on_fail: 'refuse' | 'annotate' | 'human';
  refusal: string;
  max_tool_calls: string;
  /** Say what it could not check (the runtime's default: on). */
  disclose: boolean;
}

export const EMPTY_GUARD: GuardDraft = {
  scope: '',
  grounding: 'none',
  verify: false,
  on_fail: 'refuse',
  refusal: '',
  max_tool_calls: '',
  disclose: true,
};

export function guardDraftOf(g: Record<string, unknown> | null | undefined): GuardDraft {
  if (!g) return { ...EMPTY_GUARD };
  return {
    scope: typeof g.scope === 'string' ? g.scope : '',
    grounding: g.grounding === 'tools' ? 'tools' : 'none',
    verify: g.verify === true,
    on_fail: g.on_fail === 'annotate' || g.on_fail === 'human' ? g.on_fail : 'refuse',
    refusal: typeof g.refusal === 'string' ? g.refusal : '',
    max_tool_calls: typeof g.max_tool_calls === 'number' ? String(g.max_tool_calls) : '',
    disclose: g.disclose !== false,
  };
}

/** The guardrails block of a draft, defaults dropped; null when nothing is set. */
export function guardPatch(d: GuardDraft): Record<string, unknown> | null {
  const out: Record<string, unknown> = {};
  if (d.scope.trim()) out.scope = d.scope.trim();
  if (d.grounding === 'tools') out.grounding = 'tools';
  if (d.verify) out.verify = true;
  if (d.on_fail !== 'refuse') out.on_fail = d.on_fail;
  if (d.refusal.trim()) out.refusal = d.refusal.trim();
  if (d.max_tool_calls.trim()) out.max_tool_calls = Number(d.max_tool_calls);
  if (!d.disclose) out.disclose = false;
  return Object.keys(out).length ? out : null;
}

export const guardActive = (g: Record<string, unknown> | null | undefined) =>
  !!g && !!(g.scope || g.grounding === 'tools' || g.verify || g.max_tool_calls);

/** Checks a draft the way the runtime will (the api has the final word). */
export function guardProblems(d: GuardDraft): string[] {
  const out: string[] = [];
  if (d.max_tool_calls.trim()) {
    const n = Number(d.max_tool_calls);
    if (!Number.isInteger(n) || n < 1 || n > 60) out.push('Max tool calls is a whole number from 1 to 60.');
  }
  if (d.scope.length > 4000) out.push('The scope is longer than 4000 characters.');
  if (d.refusal.length > 1000) out.push('The refusal text is longer than 1000 characters.');
  return out;
}

// ── The ledger ──────────────────────────────────────────────────────────────────────────────────

export function ledgerSummary(runs: readonly LedgerRun[]) {
  let passed = 0;
  let failed = 0;
  let refused = 0;
  for (const r of runs) {
    const v = (r.guardrail as { verdict?: string } | null)?.verdict;
    if (r.status === 'refused') refused++;
    if (v === 'pass') passed++;
    else if (v === 'fail') failed++;
  }
  return { total: runs.length, passed, failed, refused };
}

export const runVerdict = (r: LedgerRun): 'pass' | 'refused' | 'failed' | 'error' | 'none' =>
  r.status === 'refused'
    ? 'refused'
    : r.status === 'error' || r.status === 'stopped'
      ? 'error'
      : (r.guardrail as { verdict?: string } | null)?.verdict === 'fail'
        ? 'failed'
        : (r.guardrail as { verdict?: string } | null)?.verdict === 'pass'
          ? 'pass'
          : 'none';

export const ago = (iso: string | null | undefined): string => {
  if (!iso) return '';
  const diff = (Date.now() - new Date(iso).getTime()) / 1000;
  if (diff < 90) return 'just now';
  if (diff < 3600) return `${Math.round(diff / 60)} min ago`;
  if (diff < 86400) return `${Math.round(diff / 3600)} h ago`;
  return new Date(iso).toLocaleString();
};
